import React, { useCallback, useMemo, useState } from "react";
import { FullscreenTakeover, Step as StepType, StepIndicator } from "@jsq/ds/core";
import { StepNames } from "./constants";
import { DistributionWorkflowContext } from "./Context/context";
import CompleteStep from "./StepComponents/CompleteStep";
import ErrorStep from "./StepComponents/ErrorStep";
import { PaymentDetailsStep } from "./StepComponents/PaymentDetailsStep";
import { ReviewAndSubmitStep } from "./StepComponents/ReviewAndSubmitStep";
import SelectPositionsStep from "./StepComponents/SelectPositionsStep";
import { PositionRowData } from "./StepComponents/SelectPositionsStep/SelectPositionsTable/types";

export type DistributionWorkflowProps = {
  onExit: () => void;
  distributionBatchId: number;
  clientName: string;
  investmentEntityName: string;
};

export const DistributionWorkflow: React.FC<DistributionWorkflowProps> = ({
  onExit,
  distributionBatchId: initialDistributionBatchId,
  clientName: initialClientName,
  investmentEntityName: initialInvestmentEntityName,
}: DistributionWorkflowProps) => {
  const [clientName, setClientName] = useState(initialClientName);
  const [investmentEntityName, setInvestmentEntityName] = useState(initialInvestmentEntityName);
  const [distributionBatchId, setDistributionBatchId] = useState<number | null>(
    initialDistributionBatchId,
  );
  const [paymentDescription, setPaymentDescription] = useState("");
  const [scheduledPaymentProcessDate, setScheduledPaymentProcessDate] = useState<Date | null>(
    null,
  );
  const [makerNote, setMakerNote] = useState("");
  const [numberOfAvailablePositions, setNumberOfAvailablePositions] = useState(0);
  const [selectedPositions, setSelectedPositions] = useState<PositionRowData[]>([]);
  const [currentStep, setCurrentStep] = useState<StepNames>(StepNames.SelectPositions);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const onSelectPositionsBack = useCallback(() => {
    setSelectedPositions([]);
    onExit();
  }, [onExit]);

  const onSelectPositionsContinue = useCallback(() => {
    setCurrentStep(StepNames.PaymentDetails);
  }, []);

  const onPaymentDetailsBack = useCallback(() => {
    setCurrentStep(StepNames.SelectPositions);
  }, []);

  const onPaymentDetailsContinue = useCallback(() => {
    setCurrentStep(StepNames.ReviewAndSubmit);
  }, []);

  const onReviewAndSubmitBack = useCallback(() => {
    setCurrentStep(StepNames.PaymentDetails);
  }, []);

  const onReviewAndSubmitSubmit = useCallback((error?: Error) => {
    if (error) {
      setErrorMessage(error.message);
      setCurrentStep(StepNames.Error);
    } else {
      setCurrentStep(StepNames.Complete);
    }
  }, []);

  const steps = useMemo(() => {
    return [
      {
        name: StepNames.SelectPositions,
        component: SelectPositionsStep,
        onBack: onSelectPositionsBack,
        onContinue: onSelectPositionsContinue,
      },
      {
        name: StepNames.PaymentDetails,
        component: PaymentDetailsStep,
        onBack: onPaymentDetailsBack,
        onContinue: onPaymentDetailsContinue,
      },
      {
        name: StepNames.ReviewAndSubmit,
        component: ReviewAndSubmitStep,
        onBack: onReviewAndSubmitBack,
        onContinue: onReviewAndSubmitSubmit,
      },
      {
        name: StepNames.Complete,
        component: CompleteStep,
        onBack: () => {},
        onContinue: () => {},
      },
      {
        name: StepNames.Error,
        component: ErrorStep,
        onBack: () => {},
        onContinue: () => {},
      },
    ].map((step) => {
      return {
        ...step,
        id: step.name.toLowerCase().split(" ").join("_"),
      };
    });
  }, [
    onExit,
    onPaymentDetailsBack,
    onPaymentDetailsContinue,
    onReviewAndSubmitBack,
    onReviewAndSubmitSubmit,
    onSelectPositionsBack,
    onSelectPositionsContinue,
  ]);

  const currentStepIndex = steps.findIndex((step) => step.name === currentStep) ?? -1;

  const getHeaderStepStatus = useMemo(() => {
    return (stepIndex: number): StepType["status"] => {
      if (stepIndex === currentStepIndex) {
        return "active";
      } else if (stepIndex < currentStepIndex) {
        return "complete";
      }
      return "inactive";
    };
  }, [currentStepIndex]);

  const headerSteps: StepType[] = useMemo(
    () =>
      steps.map((step, index) => ({
        index,
        name: step.name,
        url: step.id, // the deprecated StepIndicator requires an url and we pass an id as a workaround
        status: getHeaderStepStatus(index),
      })),
    [getHeaderStepStatus, steps],
  );

  const indicatorSteps: StepType[] = useMemo(
    () =>
      headerSteps.filter(
        (s) => ![StepNames.Complete, StepNames.Error].includes(s.name as StepNames),
      ),
    [headerSteps],
  );

  const currentStepDetails = useMemo(() => steps[currentStepIndex], [currentStepIndex, steps]);
  const StepComponent = currentStepDetails.component;

  return (
    <DistributionWorkflowContext
      value={{
        clientName,
        setClientName,
        investmentEntityName,
        setInvestmentEntityName,
        distributionBatchId,
        setDistributionBatchId,
        paymentDescription,
        setPaymentDescription,
        scheduledPaymentProcessDate,
        setScheduledPaymentProcessDate,
        makerNote,
        setMakerNote,
        selectedPositions,
        setSelectedPositions,
        numberOfAvailablePositions,
        setNumberOfAvailablePositions,
        errorMessage,
      }}
    >
      <FullscreenTakeover>
        <FullscreenTakeover.Header title={"Create new payment"} onExit={onExit} />
        <FullscreenTakeover.Subheader>
          <StepIndicator disableStepLinks steps={indicatorSteps} />
        </FullscreenTakeover.Subheader>
        <FullscreenTakeover.Body>
          <StepComponent
            onBack={currentStepDetails.onBack}
            onContinue={currentStepDetails.onContinue}
          />
        </FullscreenTakeover.Body>
      </FullscreenTakeover>
    </DistributionWorkflowContext>
  );
};

export default DistributionWorkflow;
